import Icon from '@components/icon';
import Tooltip from '@components/tooltip';
import cn from 'clsx';
import { observer } from 'mobx-react-lite';
import React, { useRef, useState } from 'react';

import { ConfirmationPopup } from 'components/ConfirmationPopup';
import { events } from 'constants/metrics';
import { useClickOutside } from 'hooks/useClickOutside';
import { useStore } from 'hooks/useStore';
import { TSubscribedFilter } from 'store/Subscriptions/SubscribedFilter';
import { tabIds } from 'store/UIStore/UIStore';
import { withConditionalWrapping } from 'utils/hocs/withConditionalWrapping';
import { sendMetricsEvent } from 'utils/metrics';

import { Counters } from './Counters/Counters';
import { Notification } from './Notification';

const ConditionalTooltip = withConditionalWrapping(Tooltip);

import css from './Card.module.css';

export type Item = {
  id: number | string;
  name: string;
  details?: string;
  subscription?: any;
};

interface ICard<T> {
  item: T;
  isActive: boolean;
  onItemClick: (item: T) => void;
  onItemDelete?: (item: T) => void;
  onItemSubscribe?: (item: T) => void;
}

const Card = <T extends TSubscribedFilter>(props: ICard<T>) => {
  const { item, onItemClick, onItemDelete, onItemSubscribe, isActive } = props;
  const ref = useRef(null);

  const [hasActions, setHasActions] = useState(false);
  const [hasConfirmationPopup, setHasConfirmationPopup] = useState(false);

  const maxContentLength = 65;
  const isContentTooLong = item.name.trim().length > maxContentLength;
  const content = isContentTooLong ? item.name.substring(0, maxContentLength).trim() + '...' : item.name;

  const {
    app: { i18n, loading },
    profile: { isFastReg },
    ui,
  } = useStore();

  const handleClickOutside = () => {
    setHasActions(false);
  };

  const handleItemClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.currentTarget != event.target) return;
    onItemClick(item);

    if (isActiveSavedFiltersTab) {
      sendMetricsEvent(events.clickFilter);
    }
  };

  const handleItemDelete = async () => {
    onItemDelete && (await onItemDelete(item));
    setHasActions(false);
    setHasConfirmationPopup(false);
  };

  const handleItemSubscribe = () => {
    onItemSubscribe && onItemSubscribe(item);
  };

  const handleActionsButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setHasActions(true);
  };

  useClickOutside(ref, handleClickOutside);

  const isDeleting = loading.deleteSavedFilter || loading.deleteLastSearchRequest;
  const isActiveSavedFiltersTab = ui.activeTab === tabIds.savedFilters;
  const { title, buttonText } = i18n.savedFilters.deleteFilterPopup;
  const popupTitle = isActiveSavedFiltersTab ? title.fromSaved : title.fromHistory;

  return (
    <div ref={ref} className={cn(css.container, { [css.isActive]: isActive })} onClick={handleItemClick}>
      {hasConfirmationPopup && (
        <ConfirmationPopup
          buttonText={buttonText}
          className={css.confirmationPopup}
          isPopupActionLoading={isDeleting}
          title={popupTitle}
          onClose={() => {
            setHasConfirmationPopup(false);
          }}
          onConfirm={handleItemDelete}
        />
      )}

      <ConditionalTooltip
        isWithoutWrapper
        classNames={{ content: css.nameTooltipContent }}
        content={item.name}
        maxWidth={334}
        wrapIf={isContentTooLong}
      >
        <div className={css.tooltipTrigger} onClick={handleItemClick}></div>
      </ConditionalTooltip>

      <span className={cn(css.content, { [css.content_extended]: item?.instance?.hasCriticalNotifications })}>
        <Notification filter={item} />
        <Counters filter={item} />

        <Tooltip content={item.name} maxWidth={334}>
          <span className={css.name}>
            {item.isCollective && (
              <Icon className={css.humansIcon} color="var(--dark-gray)" height={14} name="ic_humans" width={14} />
            )}
            {content}
          </span>
        </Tooltip>
      </span>

      <div className={css.controls}>
        {isActiveSavedFiltersTab && item.subscription && !item?.instance?.hasCriticalNotifications && (
          <Tooltip isWithoutWrapper content={i18n.subscriptions.subButton.tooltip} maxWidth={334}>
            <button className={css.subscriptionButton} onClick={handleItemSubscribe}>
              <Icon color="var(--secondary)" height={10} name="ic_mail" width={14} />
            </button>
          </Tooltip>
        )}

        <button className={css.moreButton} onClick={handleActionsButtonClick}>
          <Icon color="currentColor" height={35} name="ic_ellipsis" width={14} />
        </button>
      </div>

      <div className={cn(css.actions, { [css.actions_visible]: hasActions })}>
        {onItemDelete && (
          <button
            className={css.actionButton}
            onClick={() => {
              setHasConfirmationPopup(true);
            }}
          >
            <Icon color="currentColor" height={26} name="ic_delete" width={26} />
            <span>{i18n.app.delete}</span>
          </button>
        )}

        <ConditionalTooltip
          content={
            <div
              className={cn(css.detailsTooltipContent)}
              dangerouslySetInnerHTML={{ __html: item.details || '' }}
            ></div>
          }
          maxWidth={448}
          wrapIf={hasActions}
        >
          <button className={cn(css.actionButton, css.infoButton)} onClick={() => {}}>
            <Icon color="currentColor" height={26} name="ic_information" width={26} />
            <span>{i18n.app.readMore}</span>
          </button>
        </ConditionalTooltip>

        {onItemSubscribe && !isFastReg && (
          <button className={css.actionButton} onClick={handleItemSubscribe}>
            <Icon color="currentColor" height={26} name="ic_mail" width={26} />
            <span>{i18n.app.subscription}</span>
          </button>
        )}

        <button
          className={css.close}
          onClick={() => {
            setHasActions(false);
          }}
        >
          <Icon color="currentColor" height={26} name="ic_close" width={26} />
        </button>
      </div>
    </div>
  );
};

const connected = observer(Card);

export { connected as Card };
